/**
 * How money, dates and phone numbers read in the MajiTap apps. Money is a decimal string from the
 * API (rule 3: never a JS number), formatted exactly: Intl takes the string as a decimal, without
 * floating point. Times are shown in the company's zone, whatever the browser's.
 */

export interface CompanyFormat {
  currency: string;
  timezone: string;
  locale?: string;
}

const DEFAULT_LOCALE = "en-KE";

/** "1234.5" in KES → "KES 1,234.50". Rounds half up to 2 places, like the API. */
export function formatMoney(
  amount: string,
  { currency, locale = DEFAULT_LOCALE }: Pick<CompanyFormat, "currency" | "locale">,
) {
  if (!/^-?\d+(\.\d+)?$/.test(amount)) throw new Error(`Not a decimal amount: ${amount}`);
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    currencyDisplay: "code",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    roundingMode: "halfExpand",
    // a decimal string is formatted exactly (Intl.NumberFormat v3), never through a float
  }).format(amount as `${number}`);
}

/** "18 Oct 2026", in the company's zone. */
export function formatDate(
  iso: string,
  { timezone, locale = DEFAULT_LOCALE }: Pick<CompanyFormat, "timezone" | "locale">,
) {
  return new Intl.DateTimeFormat(locale, {
    timeZone: timezone,
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(iso));
}

/** "18 Oct 2026, 14:05", in the company's zone. */
export function formatDateTime(
  iso: string,
  { timezone, locale = DEFAULT_LOCALE }: Pick<CompanyFormat, "timezone" | "locale">,
) {
  return new Intl.DateTimeFormat(locale, {
    timeZone: timezone,
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(new Date(iso));
}

/** E.164 for display: "+254712345678" → "+254 712 345 678". Other shapes are left as they are. */
export function formatPhone(phone: string | null | undefined): string {
  if (!phone) return "";
  const kenya = phone.match(/^\+254(\d{3})(\d{3})(\d{3})$/);
  return kenya ? `+254 ${kenya[1]} ${kenya[2]} ${kenya[3]}` : phone;
}

/** The zone's offset from UTC at an instant, in ms (positive east of Greenwich). */
function offsetAt(instant: number, timezone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(instant);
  // number-ok: calendar parts of a date, not money
  const get = (type: string) => Number.parseInt(parts.find((p) => p.type === type)?.value ?? "0", 10);
  return Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"), get("second")) - instant;
}

/**
 * The instant a wall-clock time happens in a zone, e.g. 08:00 on 19 Oct in Nairobi → 05:00Z.
 * `day` is a calendar day as pickers give it (its year, month and date are read, not its instant).
 */
export function zonedInstant(day: Date, time: string, timezone: string): string {
  // number-ok: hours and minutes of "HH:mm", not money
  const [hour, minute] = time.split(":").map((n) => Number.parseInt(n, 10));
  const wall = Date.UTC(day.getFullYear(), day.getMonth(), day.getDate(), hour, minute);
  let instant = wall - offsetAt(wall, timezone);
  // a second pass settles days when the clocks change
  instant = wall - offsetAt(instant, timezone);
  return new Date(instant).toISOString();
}

/** Whole days in the company's zone as an API range: `from` the first midnight, `to` the midnight after the last day. */
export function dayRange(range: { start: Date; end: Date }, timezone: string): { from: string; to: string } {
  const after = new Date(range.end.getFullYear(), range.end.getMonth(), range.end.getDate() + 1);
  return { from: zonedInstant(range.start, "00:00", timezone), to: zonedInstant(after, "00:00", timezone) };
}

/** The calendar day an instant falls on in a zone, as a picker's Date (local midnight of that day). */
export function calendarDay(iso: string, timezone: string): Date {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(iso));
  // number-ok: calendar parts of a date, not money
  const get = (type: string) => Number.parseInt(parts.find((p) => p.type === type)?.value ?? "0", 10);
  return new Date(get("year"), get("month") - 1, get("day"));
}

/** "2026-10-18": the calendar day an instant (default: now) falls on in a zone, as the API's `format: date`. */
export function isoDay(timezone: string, at: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(at);
}

/** A picker's Date (local midnight) as the API's `format: date`. */
export const pickerDay = (day: Date) =>
  `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`;
